import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ICoverage, Option, Term } from '@amfam/policy/models';
import { userQuery } from '@amfam/shared/user';
import { CopyService } from '@amfam/shared/utility/shared-services';

import { CoverageOptionModel, CoverageOptionModelWithControl } from '../../models/coverages.model';

@Component({
  selector: 'ds-out-of-pocket-coverages',
  templateUrl: './out-of-pocket-coverages.component.html',
  styleUrls: ['./out-of-pocket-coverages.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class OutOfPocketCoveragesComponent implements OnInit, OnChanges {
  @Input() coverages: CoverageOptionModelWithControl[];
  @Input() partnerCoverages: ICoverage[];
  @Input() form: UntypedFormGroup;
  @Input() sectionHeader: string;
  @Input() vehicleRiskDescription: string;
  @Input() state: string;
  @Input() selectedId: string;
  @Input() isPartner: boolean;
  @Output() updateCoverage = new EventEmitter<{
    selectedId: string;
    coverage: ICoverage;
    term: Term;
    option?: Option;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value?: any;
  }>();

  ersCtrl: AbstractControl;
  fullGlassCtrl: AbstractControl;
  roadTripAccidentCtrl: AbstractControl;

  public optionDrpDisabled: boolean;
  public colisionCov: boolean;
  public compresiveCov: boolean;
  public rentReamb: boolean;
  public stateCode: string;
  public UMPDStateCode: string;
  public policyDescription: string;
  additionalStyle: string;
  showHelpText = 'Show help text';
  hideHelpText = 'Hide help text';
  showComprehensiveInfo = false;
  public emergencyRoadService: string;
  public waiveOfDeductable: string;
  public glassCoverage: string;
  public optionofCoverageTerm: string;
  collisionInfoInfoHelpIconText = this.showHelpText;
  comprehensiveInfoInfoHelpIconText = this.showHelpText;
  removeNoCoverageStates: { states: string[]; coverages: string[] };
  userStateCode: Observable<string>;

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private copyService: CopyService,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.store
      .select(userQuery.selectUserStateCode)
      .pipe(takeUntil(this.stop$))
      .subscribe(ele => {
        this.UMPDStateCode = ele;
      });

    this.stateCode = this.copyService.getCopy('policy.stateCodeVarginia');
    this.policyDescription = this.copyService.getCopy('policy.policyOptionDescription');
    this.emergencyRoadService = this.copyService.getCopy('policy.paEmergencyRoadService');
    this.waiveOfDeductable = this.copyService.getCopy('policy.waiveOfDeductable');
    this.glassCoverage = this.copyService.getCopy('policy.glassCoverage');
    this.optionofCoverageTerm = this.copyService.getCopy('policy.optionofCoverageTerm');
    this.removeNoCoverageStates = this.copyService.getCopy('policy.removeNoCoverageStates');
    this.colisionCov = false;
    this.compresiveCov = false;
    this.rentReamb = false;
    this.buildForm();
  }

  /**
   * AS: TODO: This could be optimized by pulling the logic of building the forms in the parent
   * and sending the control and values downstream via Input.
   *
   * JS: This could also be fixed by wiring the whole thing back to the reducer and just dispatching actions and doing the business logic there.
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['coverages'] && !this.isPartner) {
      for (const coverage of this.coverages) {
        if (
          coverage.coverageDescription === 'Uninsured Motorist - Property Damage' &&
          this.UMPDStateCode === 'IL' &&
          !this.isPartner
        ) {
          coverage.isCheckbox = true;
          coverage.deductibleDictionary = {};
          coverage.options = undefined;
        }
        if (
          this.form.controls[coverage.coverageCode] &&
          this.form.controls[coverage.coverageCode].value !== coverage.selectedValue
        )
          this.form.controls[coverage.coverageCode].setValue(coverage.selectedValue);
      }
    }

    if (this.isPartner) this.updateFormControls();
  }

  updateFormControls() {
    this.partnerCoverages.forEach(coverage => {
      if (this.form.controls[coverage.publicID]) {
        this.form.controls[coverage.publicID].setValue(coverage.selected);
      } else {
        coverage.terms.forEach(term => {
          if (this.form.controls[coverage.publicID + term.name]) {
            this.form.controls[coverage.publicID + term.name].setValue(term.chosenTermValue);
          }
        });
      }
    });
  }

  optionEnabledDisabled(selectedValue, coverageCode, labelText) {
    if (labelText === 'Maximum' || labelText === 'Deductible') {
      if (selectedValue === 'Not Selected') {
        this.optionDrpDisabled = true;
        if (coverageCode === 'PARentalCov') {
          if (this.form.contains('PARentalCovOption'))
            this.form.controls['PARentalCovOption'].setValue('');
          this.rentReamb = true;
        } else if (coverageCode === 'PACollisionCov') {
          this.colisionCov = true;
          if (this.form.contains('PACollisionCovWaiver of Deductible'))
            this.form.controls['PACollisionCovWaiver of Deductible'].setValue('');
        } else if (coverageCode === 'PAComprehensiveCov') {
          this.compresiveCov = true;
          if (this.form.contains('PAComprehensiveCovGlass Coverage Deductible'))
            this.form.controls['PAComprehensiveCovGlass Coverage Deductible'].setValue('');
        }
      } else {
        if (coverageCode === 'PARentalCov') {
          if (this.form.contains('PARentalCovOption'))
            this.form.controls['PARentalCovOption'].setValue('Option 1');
          this.rentReamb = false;
        } else if (coverageCode === 'PACollisionCov') {
          this.colisionCov = false;
          if (this.form.contains('PACollisionCovWaiver of Deductible'))
            this.form.controls['PACollisionCovWaiver of Deductible'].setValue('Yes');
        } else if (coverageCode === 'PAComprehensiveCov') {
          this.compresiveCov = false;
          if (this.form.contains('PAComprehensiveCovGlass Coverage Deductible'))
            this.form.controls['PAComprehensiveCovGlass Coverage Deductible'].setValue('100');
        }
        this.optionDrpDisabled = false;
      }
    }
  }

  toggleComprehensiveInfo() {
    this.showComprehensiveInfo = !this.showComprehensiveInfo;
    this.additionalStyle = this.showComprehensiveInfo ? 'extra-pad-top' : '';
    this.comprehensiveInfoInfoHelpIconText = this.showComprehensiveInfo
      ? this.hideHelpText
      : this.showHelpText;
  }

  getDeductible(coverage: CoverageOptionModel) {
    const value = this.form.controls[`${coverage.coverageCode}`].value;
    return coverage?.control?.options.filter(o => o.name === value)[0]?.deductible;
  }

  hasDeductible(coverage: CoverageOptionModel) {
    const value = this.form.controls[`${coverage.coverageCode}`].value;
    return coverage?.control?.options.filter(o => o.name === value)[0]?.deductible ? true : false;
  }

  toggleCtrl(controlName) {
    this.form.controls[controlName].setValue(!this.form.controls[controlName].value);
  }

  getControlName(coverage) {
    const controlName =
      coverage.extraOptions && coverage.extraOptions.length > 0
        ? coverage.coverageCode + ':' + coverage.coverageDescription
        : coverage.coverageCode;
    const control = this.form.controls[controlName];
    return control ? controlName : coverage.coverageCode + ':' + coverage.coverageDescription;
  }

  canShowExtraOption(coverage) {
    if (
      coverage.coverageCode === 'PACollisionCov' &&
      this.form.controls[coverage.coverageCode + ':' + coverage.coverageDescription] &&
      this.form.controls[coverage.coverageCode + ':' + coverage.coverageDescription].value ===
        'Not Selected'
    ) {
      return false;
    }
    return !this.canShowNoDeductible(coverage);
  }
  getExtraOptions(coverage) {
    if (coverage.coverageCode === 'PACollisionCov') {
      return coverage.extraOptions.filter(opt => opt !== 'No Deductible');
    }
    return coverage.extraOptions;
  }
  canShowNoDeductible(coverage) {
    if (coverage.extraOptions && coverage.extraOptions.length > 0) {
      if (
        coverage.coverageCode === 'PACollisionCov' &&
        this.form.controls[coverage.coverageCode + ':' + coverage.coverageDescription] &&
        this.form.controls[coverage.coverageCode + ':' + coverage.coverageDescription].value ===
          'Limited Option without Deductible'
      ) {
        this.form.controls[
          coverage.coverageCode + ':' + coverage.coverageExtraDescription
        ].setValue('No Deductible');
        return true;
      }
    }
    return false;
  }
  canShowComprehensiveOption(coverage) {
    if (
      coverage.coverageCode === 'PAComprehensiveCov' &&
      this.form.controls[coverage.coverageCode + ':' + coverage.coverageDescription] &&
      this.form.controls[coverage.coverageCode + ':' + coverage.coverageDescription].value ===
        'Not Selected'
    ) {
      return false;
    }
    return true;
  }
  canEnablePAComprehensiveCov() {
    if (
      this.form.controls[
        'PAComprehensiveCov' + ':' + 'PAComprehensiveCov:Comprehensive (Part 9)'
      ] &&
      this.form.controls['PAComprehensiveCov' + ':' + 'PAComprehensiveCov:Comprehensive (Part 9)']
        .value !== 'Not Selected'
    ) {
      return false;
    }
    return true;
  }
  canEnablePALimitedCollisionCovAf() {
    if (
      this.form.controls['PACollisionCov' + ':' + 'PACollisionCov:Collision (Part 7)'] &&
      this.form.controls['PACollisionCov' + ':' + 'PACollisionCov:Collision (Part 7)'].value !==
        'Not Selected'
    ) {
      return false;
    }
    return true;
  }

  update(coverage: ICoverage) {
    this.updateCoverage.emit({
      selectedId: this.selectedId,
      coverage: coverage,
      term: undefined,
      option: undefined,
      value: this.form.controls[coverage.publicID].value
    });
  }

  /**
   * @description
   * @author @jshaw-af
   * @date 17/03/2022
   * @private
   * @param {ICoverage[]} coverages
   * @memberof OutOfPocketCoveragesComponent
   */
  private buildPartnerControls(coverages: ICoverage[]) {
    for (const coverage of coverages) {
      if (!coverage.hasTerms || coverage.publicID === this.emergencyRoadService) {
        this.form.addControl(coverage.publicID, new UntypedFormControl(coverage.selected));
      }
      coverage.terms.forEach(term => {
        this.form.addControl(
          coverage.publicID + term.name,
          new UntypedFormControl(term.chosenTermValue)
        );
      });
    }
  }

  private buildForm() {
    for (const coverage of this.coverages) {
      if (
        coverage.coverageDescription === 'Uninsured Motorist - Property Damage' &&
        this.UMPDStateCode === 'IL' &&
        !this.isPartner
      ) {
        coverage.isCheckbox = true;
        coverage.deductibleDictionary = {};
        coverage.options = undefined;
      }
    }
    if (!this.isPartner) this.buildControls(this.coverages);
    else this.buildPartnerControls(this.partnerCoverages);
  }

  /**
   * @author: Abhishek Singh
   * @param form : Takes a form group and adds the new controls to the form
   * @returns FormGroup : Return the form which has the new controls and the initialized values.
   */
  private buildControls(coverages: CoverageOptionModel[]) {
    for (const coverage of coverages) {
      this.form.addControl(coverage.coverageCode, new UntypedFormControl(coverage.selectedValue));
    }
  }
}
